import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from '@alltrails/shared/react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import PlusBadge from '@alltrails/shared/denali/components/PlusBadge';
import Play from '@alltrails/shared/icons/Play';
import useToggle from '@alltrails/shared/hooks/useToggle';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import logFlyoverStartButtonTapped from '@alltrails/analytics/events/logFlyoverStartButtonTapped';
import FlyoverSource from '@alltrails/analytics/enums/FlyoverSource';
import SignupModal from 'components/SignupModal';
import useMobileWidth from 'hooks/useMobileWidth';
import useUser from 'hooks/useUser';
import { TrailId } from 'types/Trails';
import * as styles from './styles.module.scss';

const messages = defineMessages({
  PREVIEW_ROUTE: {
    defaultMessage: 'Preview route'
  }
});

type FlyoverEntryProps = {
  exploreFlag?: boolean;
  exactExplorePath: string;
  initializingFlyover?: boolean;
  onEntryClick?: () => void;
  trailId: TrailId;
};

const FlyoverEntry = ({ exploreFlag, exactExplorePath, initializingFlyover = false, onEntryClick, trailId }: FlyoverEntryProps): JSX.Element => {
  const {
    formattedDefaultMessages: { PREVIEW_ROUTE }
  } = useFormatMessage(messages);

  const user = useUser();
  const isMobileWidth = useMobileWidth();

  const [isOpen, toggle] = useToggle(false);
  const [signedInThroughModal, setSignedInThroughModal] = useState(false);
  const [showOfflineError, setShowOfflineError] = useState(false);

  const flyoverParam = 'initFlyover=true';
  const flyoverHref = exactExplorePath.indexOf('?') === -1 ? `${exactExplorePath}?${flyoverParam}` : `${exactExplorePath}&${flyoverParam}`;
  const linkInfo = !!user?.pro && !exploreFlag ? { href: flyoverHref } : null;

  const onFlyoverEntryClick = (ev: React.MouseEvent) => {
    logFlyoverStartButtonTapped({ source: exploreFlag ? FlyoverSource.MapDetails : FlyoverSource.TrailDetails, trail_id: trailId });
    setShowOfflineError(false);
    if (!window.navigator.onLine) {
      ev.preventDefault();
      setShowOfflineError(true);
      return;
    }

    if (!user) {
      toggle();
    } else if (!user.pro) {
      // TODO: TRAILS-825 pro upsell
    } else if (user.pro) {
      onEntryClick?.();
    }
  };

  useEffect(() => {
    // initialize flyover after successful login
    if (exploreFlag && signedInThroughModal && exploreFlag && user?.pro) {
      onEntryClick?.();
      setSignedInThroughModal(false);
    }
  }, [exploreFlag, flyoverHref, onEntryClick, signedInThroughModal, user?.pro]);

  return (
    <div className={styles.flyoverContainer}>
      <SignupModal
        isOpen={isOpen}
        onRequestClose={toggle}
        onSuccess={() => {
          toggle();
          setSignedInThroughModal(true);
        }}
        redirectUrl={exploreFlag ? null : flyoverHref}
      />
      <div className={styles.buttonEntry}>
        <Button
          icon={{ Component: Play }}
          loading={initializingFlyover}
          linkInfo={linkInfo}
          onClick={onFlyoverEntryClick}
          testId="flyover-entry"
          text={PREVIEW_ROUTE}
          size="md"
          width={isMobileWidth || exploreFlag ? 'full' : 'auto'}
        />
        <PlusBadge className={styles.plusBadge} />
      </div>
      {showOfflineError && (
        <span className={styles.error}>
          <FormattedMessage defaultMessage="We’re unable to  submit your request because you’re offline. Please try again later." />
        </span>
      )}
    </div>
  );
};

export default FlyoverEntry;
